/// <reference path='common.ts' />
/// <reference path='fetch.ts' />
/// <reference path='element_form.ts' />
/// <reference path='element_load.ts' />

/**
 * エレメントをクリックした際の処理を定義します。
 *
 * 属性
 * data-click-confirm: クリックした際に表示される確認ダイアログのメッセージ
 * data-click-fetch: クリックされた際にフェッチするURL
 * data-click-data: フェッチ結果の代わりに使用されるデータ（JSON形式）
 * data-click-method: フェッチする際のメソッド（省略時はGET）
 * data-click-enctype: フェッチする際に送信するContent-Type（GETメソッドの場合はapplication/x-www-form-urlencoded、それ以外場合はapplication/jsonがデフォルト）
 * data-click-form: フェッチする際に付与するパラメータが格納されているフォームのセレクタ（値が空の場合は親フォーム）フェッチしない場合はパラメータが値として利用される
 * data-click-reset: クリックした際もしくはフェッチに成功した際にリセットするフォームのセレクタ（値が省略されている場合はdata-click-formの値を使う）
 * data-click-param: フェッチする際に送信するパラメータ（JSON形式）（data-click-formとは排他）
 * data-click-bind: フェッチ結果を属性として格納するエレメントのセレクタ（値が空の場合は当該エレメント）
 * data-click-bind-attribute: フェッチ結果を格納する属性（省略時はdata-bind）
 * data-click-bind-arg: フェッチ結果を格納する変数（省略時は展開する）
 * data-click-bind-merge: フェッチ結果を元のdeta-bindとマージする
 * data-click-redirect: クリックした際もしくはフェッチに成功した際に遷移するURL
 * data-click-alert: クリックした際もしくはフェッチに成功した際に表示するダイアログのメッセージ
 * data-click-message: クリックした際もしくはフェッチに成功した際に表示するメッセージ
 * data-click-open: クリックした際もしくはフェッチに成功した際に開くダイアログのセレクタ（値が空の場合は親ダイアログ）
 * data-click-close: クリックした際もしくはフェッチに成功した際に閉じるダイアログのセレクタ（値が空の場合は親ダイアログ）
 * data-click-click: クリックした際もしくはフェッチに成功した際にクリックするエレメントのセレクタ
 * data-click-bind-reload: クリックした際もしくはフェッチに成功した際にリロードするdata-bind-fetch属性があるエレメントのセレクタ
 * data-click-table-reload: クリックした際もしくはフェッチに成功した際にリロードするテーブルエレメントのセレクタ
 * data-click-download: バイナリデータをフェッチに成功した場合に保存するファイル名
 * data-click-copy: フェッチした内容をコピーして設定するエレメントのセレクタ
 * data-click-cache-use: キャッシュした内容をフェッチの代わりに使用する場合（値をキーとし、設定していない場合はフェッチURLをキーとする）
 * data-click-cache-set: フェッチした内容をキャッシュする（値をキーとし、設定していない場合はフェッチURLをキーとする）
 * data-click-cache-clear: クリックした際もしくはフェッチに成功した際にキャッシュをクリアする（値をキーとし、設定していない場合はフェッチURLをキーとする）
 * data-click-increment: クリックした際に値を増加させる入力欄のセレクタ
 * data-click-decrement: クリックした際に値を減少させる入力欄のセレクタ
 * data-click-search-param: クリックした際にURLのパラメータを変更します（key1=param1&key2=param2の形式で指定してください）
 * data-click-scroll: クリックした際もしくはフェッチに成功した際に対象のセレクタがあるエレメントが画面に表示されるまでスクロールします（対象エレメントは一つとします）
 *
 * イベント
 * fetch: 通信が完了した場合（detailはrequestとresponseのPromise）
 * fetch-error: 通信中にエラーが発生した場合（detailはrequestとエラー内容）
 */
class HaoriClick {
  /**
   * エレメントのクリック処理を実行します。
   *
   * @param target クリックイベントもしくは対象エレメント
   */
  static async click(target: Event | Element) {
    let event: Event | null;
    let element: Element | null;
    if (target instanceof Event) {
      element = HaoriClick.getTargetElement(<HTMLElement>target.target);
      event = target;
    } else {
      element = <Element>target;
      event = null;
    }
    if (element == null) {
      return;
    }

    let result = await Haori.hasAttribute(element, 'data-click-confirm', async (element, value) => {
      event?.preventDefault();
      return await Haori.confirm(value);
    });
    if (result === false) {
      return;
    }

    let fetch = new HaoriFetch();
    fetch.setParams(element, 'data-click');
    let fetched = Haori.hasAttribute(element, 'data-click-fetch', (target, url) => {
      event?.preventDefault();
      fetch.fetch(url, element);
      return true;
    });
    if (fetched === null) {
      let bind: any = null;
      Haori.hasAttribute(element, 'data-click-data', (target, data) => {
        event?.preventDefault();
        bind = JSON.parse(data);
      });
      let form: HTMLFormElement | null = null;
      Haori.hasAttribute(element, 'data-click-form', (element, value) => {
        if (value == '') {
          form = <HTMLFormElement>element.closest('form');
        } else {
          form = <HTMLFormElement>Haori.querySelector(value);
        }
      });
      if (form) {
        bind = HaoriForm.getValues(form);
      }
      fetch.receive(bind).then((result) => {
        if (result) {
          event?.preventDefault();
        }
      });
    }

    Haori.hasAttribute(element, 'data-click-increment', (target, value) => {
      event?.preventDefault();
      let element = Haori.querySelector(value);
      HaoriClick.changeValue(<HTMLInputElement>element, true);
    });

    Haori.hasAttribute(element, 'data-click-decrement', (target, value) => {
      event?.preventDefault();
      let element = Haori.querySelector(value);
      HaoriClick.changeValue(<HTMLInputElement>element, false);
    });

    Haori.hasAttribute(element, 'data-click-search-param', (target, value) => {
      let search: URLSearchParams;
      if (location.search.length == 0) {
        search = new URLSearchParams();
      } else {
        search = new URLSearchParams(location.search.substring(1));
      }
      let params = new URLSearchParams(value);
      params.forEach((value, key) => {
        search.delete(key);
        search.append(key, value);
      });
      let newParams = search.toString();
      if (location.search != '?' + newParams) {
        history.pushState({}, '', location.pathname + '?' + newParams);
        HaoriLoad.reload();
      }
    });
  }

  /**
   * data-click- から始まるエレメントを対象エレメントから遡って最初に見つかるところまで探します。
   * 存在しない場合は null を返します。
   *
   * @param element エレメント
   */
  private static getTargetElement(element: HTMLElement): HTMLElement | null {
    let find = false;
    element.getAttributeNames().forEach((name) => {
      if (name.indexOf('data-click-') == 0) {
        find = true;
      }
    });
    if (find) {
      return element;
    }
    let parent = element.parentElement;
    if (parent == null) {
      return null;
    }
    return this.getTargetElement(parent);
  }

  /**
   * inputエレメントの値を増加もしくは減少します。
   *
   * @param element 対象エレメント
   * @param isIncrement 増加させる場合はtrue
   */
  private static changeValue(element: HTMLInputElement, isIncrement: boolean) {
    let type = element.getAttribute('type');
    let currentValue = element.value;
    let newValue = currentValue;
    switch (type) {
      case 'number': {
        if (element.value == '') {
          newValue = '0';
          break;
        }
        let value = Number(element.value || element.getAttribute('min'));
        let step = Number(element.getAttribute('step') || '1');
        if (isIncrement) {
          value = value + step;
        } else {
          value = value - step;
        }
        value = Math.round(value * 100000000) / 100000000.0; // 丸め誤差を修正する
        if (element.getAttribute('min') != null) {
          let min = Number(<string>element.getAttribute('min'));
          if (value < min) {
            value = min;
          }
        }
        if (element.getAttribute('max') != null) {
          let max = Number(<string>element.getAttribute('max'));
          if (max < value) {
            value = max;
          }
        }
        newValue = value.toString();
        break;
      }
      case 'date': {
        if (element.value == '') {
          newValue = new Date()
            .toLocaleDateString('ja-JP', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            })
            .split('/')
            .join('-');
          break;
        }
        let value = new Date(element.value);
        let step = parseInt(element.getAttribute('step') || '1');
        if (isIncrement) {
          value.setDate(value.getDate() + step);
        } else {
          value.setDate(value.getDate() - step);
        }
        if (element.getAttribute('min') != null) {
          let min = new Date(<string>element.getAttribute('min'));
          if (value < min) {
            value = min;
          }
        }
        if (element.getAttribute('max') != null) {
          let max = new Date(<string>element.getAttribute('max'));
          if (max < value) {
            value = max;
          }
        }
        newValue = value
          .toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
          .split('/')
          .join('-');
        break;
      }
      case 'month': {
        if (element.value == '') {
          newValue = new Date()
            .toLocaleDateString('ja-JP', {
              year: 'numeric',
              month: '2-digit',
            })
            .split('/', 2)
            .join('-');
          break;
        }
        let value = new Date(element.value + '-01');
        let step = parseInt(element.getAttribute('step') || '1');
        if (isIncrement) {
          value.setMonth(value.getMonth() + step);
        } else {
          value.setMonth(value.getMonth() - step);
        }
        if (element.getAttribute('min') != null) {
          let min = new Date(<string>element.getAttribute('min') + '-01');
          if (value < min) {
            value = min;
          }
        }
        if (element.getAttribute('max') != null) {
          let max = new Date(<string>element.getAttribute('max') + '-01');
          if (max < value) {
            value = max;
          }
        }
        newValue = value
          .toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
          })
          .split('/', 2)
          .join('-');
        break;
      }
      default:
        console.error('type属性の値(' + type + ')はサポートしていません');
    }
    if (currentValue != newValue) {
      element.value = newValue;
      element.dispatchEvent(
        new Event('change', {
          bubbles: true,
          cancelable: true,
          composed: true,
        })
      );
    }
  }
}

document.body.addEventListener('click', HaoriClick.click);
